import { useState, useEffect } from "react";
import { api } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Save, Plus, Edit, Trash2, MapPin, Globe, FileText } from "lucide-react";
import ProposalTermsEditor from "@/components/settings/ProposalTermsEditor";

export default function CompanySettings({ user }) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("company");
  
  // Company Info State
  const [formData, setFormData] = useState({
    company_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "USA",
    phone: "",
    email: "",
    website: "",
    proposal_responsible_user: "",
    invoice_responsible_user: "",
    timesheet_responsible_user: ""
  });

  // Branches State
  const [branches, setBranches] = useState([]);
  const [countries, setCountries] = useState([]);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchFormData, setBranchFormData] = useState({
    name: "",
    country: "",
    is_active: true
  });

  useEffect(() => {
    fetchCompanyInfo();
    fetchUsers();
    fetchBranches();
    fetchCountries();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await api.get("/company-info");
      if (response.data) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error("Error fetching company info:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await api.get("/branches?include_inactive=true");
      setBranches(response.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await api.get("/country-config");
      setCountries(response.data || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountries([]);
    }
  };

  // Format phone number as XXX-XXX-XXXX
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/company-info", formData);
      toast.success("Company information saved successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save company information");
    } finally {
      setLoading(false);
    }
  };

  // Branch CRUD Operations
  const openAddBranchDialog = () => {
    setEditingBranch(null);
    setBranchFormData({ name: "", country: "", is_active: true });
    setBranchDialogOpen(true);
  };

  const openEditBranchDialog = (branch) => {
    setEditingBranch(branch);
    setBranchFormData({
      name: branch.name,
      country: branch.country,
      is_active: branch.is_active
    });
    setBranchDialogOpen(true);
  };

  const handleBranchSubmit = async () => {
    if (!branchFormData.name || !branchFormData.country) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingBranch) {
        await api.put(`/branches/${editingBranch.id}`, branchFormData);
        toast.success("Branch updated successfully");
      } else {
        await api.post("/branches", branchFormData);
        toast.success("Branch created successfully");
      }
      setBranchDialogOpen(false);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save branch");
    }
  };

  const handleDeleteBranch = async (branch) => {
    if (!confirm(`Are you sure you want to deactivate "${branch.name} (${branch.country})"? Existing data will remain linked.`)) {
      return;
    }

    try {
      await api.delete(`/branches/${branch.id}`);
      toast.success("Branch deactivated successfully");
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate branch");
    }
  };

  const handleReactivateBranch = async (branch) => {
    try {
      await api.put(`/branches/${branch.id}`, { is_active: true });
      toast.success("Branch reactivated successfully");
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate branch");
    }
  };

  const seedDefaultBranches = async () => {
    try {
      const response = await api.post("/branches/seed-defaults");
      toast.success(response.data.message);
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to seed branches");
    }
  };

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-md3-display-small flex items-center gap-2">
            <Building2 className="h-8 w-8" />
            Company Settings
          </h1>
          <p className="text-md3-on-surface-variant mt-2">Configure your company information and master data</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-md3-surface-container">
            <TabsTrigger value="company" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Company Info
            </TabsTrigger>
            <TabsTrigger value="branches" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Branches
            </TabsTrigger>
            <TabsTrigger value="proposal-terms" className="flex items-center gap-2" data-testid="proposal-terms-tab">
              <FileText className="h-4 w-4" />
              Proposal Terms
            </TabsTrigger>
          </TabsList>

          <TabsContent value="proposal-terms">
            <ProposalTermsEditor user={user} />
          </TabsContent>

          {/* Company Information Tab */}
          <TabsContent value="company">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface">Company Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Company Name */}
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-md3-label-large text-gray-700 font-medium">Company Name *</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                      placeholder="e.g., Milekal Inc."
                    />
                  </div>

                  {/* Address */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="address_line1" className="text-md3-label-large text-gray-700 font-medium">Address Line 1 *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="address_line1"
                        value={formData.address_line1}
                        onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                        required
                        placeholder="Street address"
                      />
                    </div>

                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="address_line2" className="text-md3-label-large text-gray-700 font-medium">Address Line 2</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="address_line2"
                        value={formData.address_line2}
                        onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                        placeholder="Suite, unit, building (optional)"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-md3-label-large text-gray-700 font-medium">City *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                        placeholder="City"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="state" className="text-md3-label-large text-gray-700 font-medium">State *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        required
                        placeholder="State"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="zip_code" className="text-md3-label-large text-gray-700 font-medium">ZIP Code *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="zip_code"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        required
                        placeholder="ZIP"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-md3-label-large text-gray-700 font-medium">Country *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="country"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        required
                        placeholder="Country"
                      />
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-md3-label-large text-gray-700 font-medium">Phone *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        required
                        placeholder="XXX-XXX-XXXX"
                        maxLength={12}
                      />
                      <p className="text-xs text-md3-on-surface-variant">US format (10 digits)</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-md3-label-large text-gray-700 font-medium">Email *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        placeholder="contact@company.com"
                      />
                    </div>
                  </div>

                  {/* Website */}
                  <div className="space-y-2">
                    <Label htmlFor="website" className="text-md3-label-large text-gray-700 font-medium">Website</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="website"
                      type="text"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="www.company.com"
                    />
                    <p className="text-xs text-md3-on-surface-variant">Enter without https:// (e.g., www.company.com)</p>
                  </div>

                  {/* Divider */}
                  <div className="border-t pt-4 mt-6">
                    <h3 className="text-md3-title-large mb-4">Activity Responsible Users</h3>
                    <p className="text-md3-body-small text-md3-on-surface-variant mb-4">Assign users responsible for different activities</p>
                  </div>

                  {/* Responsible Users */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="proposal_responsible" className="text-md3-label-large text-gray-700 font-medium">Proposal Generation</Label>
                      {users.length > 0 ? (
                        <Select 
                          value={formData.proposal_responsible_user || "none"} 
                          onValueChange={(value) => setFormData({ ...formData, proposal_responsible_user: value === "none" ? null : value })}
                        >
                          <SelectTrigger id="proposal_responsible">
                            <SelectValue placeholder="Select user" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.first_name} {u.last_name || ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value="Loading users..." disabled />
                      )}
                      <p className="text-xs text-md3-on-surface-variant">User responsible for proposals</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="invoice_responsible" className="text-md3-label-large text-gray-700 font-medium">Invoicing</Label>
                      {users.length > 0 ? (
                        <Select 
                          value={formData.invoice_responsible_user || "none"} 
                          onValueChange={(value) => setFormData({ ...formData, invoice_responsible_user: value === "none" ? null : value })}
                        >
                          <SelectTrigger id="invoice_responsible">
                            <SelectValue placeholder="Select user" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.first_name} {u.last_name || ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value="Loading users..." disabled />
                      )}
                      <p className="text-xs text-md3-on-surface-variant">User responsible for invoices</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="timesheet_responsible" className="text-md3-label-large text-gray-700 font-medium">Timesheet Management</Label>
                      {users.length > 0 ? (
                        <Select 
                          value={formData.timesheet_responsible_user || "none"} 
                          onValueChange={(value) => setFormData({ ...formData, timesheet_responsible_user: value === "none" ? null : value })}
                        >
                          <SelectTrigger id="timesheet_responsible">
                            <SelectValue placeholder="Select user" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.first_name} {u.last_name || ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value="Loading users..." disabled />
                      )}
                      <p className="text-xs text-md3-on-surface-variant">User responsible for timesheets</p>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button type="submit" disabled={loading} className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                    <Save className="mr-2 h-4 w-4" />
                    {loading ? "Saving..." : "Save Company Information"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branches Tab */}
          <TabsContent value="branches">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-md3-title-large text-md3-on-surface">Branch Master</CardTitle>
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                    Manage company branches linked to countries
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={seedDefaultBranches}>
                    Seed Defaults
                  </Button>
                  <Button onClick={openAddBranchDialog} className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Branch
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {countries.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-amber-800 text-sm">
                      <strong>Note:</strong> No countries configured. Please add countries in{" "}
                      <a href="/expense/settings" className="text-blue-600 underline">Expense Settings → Country Config</a> first.
                    </p>
                  </div>
                )}
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Branch Name</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {branches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                          No branches configured. Click "Seed Defaults" or "Add Branch" to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      branches.map((branch) => (
                        <TableRow key={branch.id} className={!branch.is_active ? "opacity-60" : ""}>
                          <TableCell className="font-medium">{branch.name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4 text-gray-400" />
                              {branch.country}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {branch.name} ({branch.country})
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={branch.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}>
                              {branch.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openEditBranchDialog(branch)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              {branch.is_active ? (
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteBranch(branch)} className="text-red-600 hover:text-red-700">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button variant="ghost" size="sm" onClick={() => handleReactivateBranch(branch)} className="text-green-600 hover:text-green-700">
                                  Reactivate
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Branch Add/Edit Dialog */}
        <Dialog open={branchDialogOpen} onOpenChange={setBranchDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingBranch ? "Edit Branch" : "Add Branch"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Branch Name *</Label>
                <Input
                  value={branchFormData.name}
                  onChange={(e) => setBranchFormData({ ...branchFormData, name: e.target.value })}
                  placeholder="e.g., Hyderabad"
                />
              </div>
              <div className="space-y-2">
                <Label>Country *</Label>
                <Select
                  value={branchFormData.country}
                  onValueChange={(value) => setBranchFormData({ ...branchFormData, country: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => (
                      <SelectItem key={c.country} value={c.country}>
                        {c.country} ({c.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Countries are managed in Expense Settings → Country Config
                </p>
              </div>
              {editingBranch && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={branchFormData.is_active ? "active" : "inactive"}
                    onValueChange={(value) => setBranchFormData({ ...branchFormData, is_active: value === "active" })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBranchDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleBranchSubmit} className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary">
                {editingBranch ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
